import { useMutation } from '@tanstack/react-query'
import { assetReferences } from '../../../v1/assetReferences'
import { assets } from '../../../v2/assets'
import { boards } from '../../../v3/boards'
import { moments } from '../../../v3/moments'
import { globalQueryKeys } from '../../queryKeys'

type AssetUploadMutationParams = {
  boardId?: string
  collectionId?: string | null
  entityId: string | null
  file: File
}

type UseAssetUploadParams = {
  entityResourceType?: string
}

export const useAssetUpload = ({
  entityResourceType,
}: UseAssetUploadParams) => {
  return useMutation({
    mutationKey: globalQueryKeys.assetUploadForEntity(entityResourceType),
    mutationFn: async ({ entityId, file }: AssetUploadMutationParams) => {
      // Post to Asset Service
      const asset = await assets.create(file)
      // Post to Asset Reference
      const assetReference = await assetReferences.create({
        requestBody: {
          data: {
            type: 'asset_reference',
            attributes: {
              asset_uuid: asset.data.id,
              filename: asset.data.attributes.file_name,
              filesize: asset.data.attributes.bytes,
              full_url: asset.data.attributes.url,
              height: asset.data.attributes.height ?? undefined,
              width: asset.data.attributes.width ?? undefined,
            },
            relationships: {},
          },
        },
      })

      // Update Moment w/ Asset Reference Relationship
      if (entityId && entityResourceType === 'moment') {
        await moments.update({
          requestBody: {
            data: {
              id: entityId,
              type: 'moment',
              attributes: {},
              relationships: {
                primary_asset_reference: {
                  data: {
                    id: assetReference.data.attributes.uuid ?? '',
                    type: 'asset_reference',
                  },
                },
              },
            },
          },
          params: {
            path: {
              moment_id: entityId,
            },
            query: {},
          },
        })
      }

      // Update Board w/ Asset Reference Relationship
      if (entityId && entityResourceType === 'board') {
        await boards.update({
          requestBody: {
            data: {
              id: entityId,
              type: 'board',
              attributes: {},
              relationships: {
                asset_reference: {
                  data: {
                    id: assetReference.data.attributes.uuid ?? '',
                    type: 'asset_reference',
                  },
                },
              },
            },
          },
          params: {
            path: {
              board_id: entityId,
            },
            query: {
              include: ['asset_reference'],
            },
          },
        })
      }

      return { asset, assetReference }
    },
  })
}
